import { HelpCircle, Cpu, Receipt } from "lucide-react";

const faq = [
  {
    slug: "general",
    section: "faq",
    title: "General FAQ",
    subtitle:
      "Answers to the most common questions about what Acadence is, who it serves, and how to get started.",
    icon: HelpCircle,
    readTime: "5 min read",
    content: [
      {
        type: "heading",
        level: 2,
        text: "What is Acadence?",
      },
      {
        type: "text",
        body: "Acadence is a cloud-based Education Management System (ERP) designed for higher-education institutions — colleges, universities, and vocational institutes. It consolidates 21 functional modules (admissions, attendance, grades, fees, HR, placements, and more) into a single platform with six role-based portals.",
      },
      {
        type: "heading",
        level: 2,
        text: "Who is Acadence designed for?",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "Degree colleges and universities (100 to 100,000+ students)",
          "Vocational and technical institutes",
          "Multi-campus institutions that need centralised data management",
          "Institutions currently using disconnected spreadsheets or legacy systems looking to modernise",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "How do I get started?",
      },
      {
        type: "steps",
        items: [
          {
            title: "Request a demo",
            description:
              "Fill out the demo request form on the Acadence website. Our team will contact you within one business day to schedule a live walkthrough.",
          },
          {
            title: "Onboarding call",
            description:
              "An Acadence implementation specialist walks you through the platform, answers your questions, and collects your institution's configuration requirements.",
          },
          {
            title: "Data migration",
            description:
              "Our team helps migrate existing student records, staff data, and historical fee data from spreadsheets or legacy systems into Acadence.",
          },
          {
            title: "Go live",
            description:
              "User accounts are activated, roles assigned, and your institution goes live. Dedicated support is available for the first 30 days.",
          },
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Is training provided?",
      },
      {
        type: "text",
        body: "Yes. Every Acadence subscription includes onboarding training for administrators and department heads. Role-specific training materials (video walkthroughs, this documentation site, and downloadable guides) are available to all users at no extra cost.",
      },
      {
        type: "callout",
        variant: "info",
        title: "Live Training Sessions",
        body: "Optional live training webinars are available for faculty, students, and HR staff. These are scheduled during your onboarding period and can be recorded for future reference.",
      },
      {
        type: "heading",
        level: 2,
        text: "Can I request a demo?",
      },
      {
        type: "text",
        body: "Absolutely. Visit the Acadence website and click 'Request a Demo'. You can also contact our sales team directly. Demo environments are fully functional — you can explore all modules with sample data before committing.",
      },
      {
        type: "heading",
        level: 2,
        text: "What support options are available?",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "Documentation site — this site covers every module in detail",
          "Email support — respond within 24 hours on business days",
          "WhatsApp support — quick queries answered during business hours",
          "Dedicated account manager — available on Enterprise plans",
          "Priority phone support — available on Enterprise plans",
          "Community forum — share tips and solutions with other Acadence administrators",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "How long does onboarding take?",
      },
      {
        type: "text",
        body: "For a typical institution with 500–5,000 students, onboarding takes 2–4 weeks. This includes data migration, configuration, user account creation, and training. Larger or more complex institutions may require 6–8 weeks.",
      },
      {
        type: "callout",
        variant: "success",
        title: "Fast-Track Option",
        body: "Institutions with clean, structured data (CSV exports from existing systems) can go live in as little as 5 business days with our fast-track onboarding package.",
      },
    ],
  },
  {
    slug: "technical",
    section: "faq",
    title: "Technical FAQ",
    subtitle:
      "Browser compatibility, mobile support, security, integrations, and self-hosting questions answered.",
    icon: Cpu,
    readTime: "5 min read",
    content: [
      {
        type: "heading",
        level: 2,
        text: "Which browsers are supported?",
      },
      {
        type: "text",
        body: "Acadence is tested and fully supported on the following browsers:",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "Google Chrome 110+ (recommended)",
          "Mozilla Firefox 110+",
          "Microsoft Edge 110+",
          "Apple Safari 16+",
        ],
      },
      {
        type: "callout",
        variant: "warning",
        title: "Internet Explorer",
        body: "Internet Explorer is not supported. If your institution requires IE11 compatibility, please contact us to discuss options.",
      },
      {
        type: "heading",
        level: 2,
        text: "Is Acadence available on mobile devices?",
      },
      {
        type: "text",
        body: "Yes. Acadence is a fully responsive web application. The interface adapts automatically for phones and tablets. A dedicated mobile app is on the product roadmap for a future release. In the meantime, users can add Acadence to their phone's home screen as a Progressive Web App (PWA) for a native-like experience.",
      },
      {
        type: "heading",
        level: 2,
        text: "How is my institution's data secured?",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "All data is encrypted in transit using TLS 1.3",
          "Passwords are hashed using bcrypt with a strong salt factor — never stored in plain text",
          "Authentication uses HttpOnly, SameSite JWT cookies — not localStorage — to prevent XSS token theft",
          "Role-based access control ensures users can only access data within their permission scope",
          "MongoDB data is encrypted at rest on cloud deployments",
          "Daily automated backups with 30-day retention",
          "IP allowlisting and two-factor authentication available on Enterprise plans",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "What integrations does Acadence support?",
      },
      {
        type: "text",
        body: "Acadence currently integrates with or supports export to the following systems:",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "WhatsApp (via Baileys API) — automated notifications and alerts",
          "Email (SMTP) — transactional emails for OTPs, fee reminders, and exam alerts",
          "Payment gateways — Razorpay and PayU for online fee collection (India); Stripe for international",
          "CSV / Excel export — all reports and data tables support one-click CSV/Excel download",
          "REST API — a documented REST API is available for custom integrations on Enterprise plans",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Can we self-host Acadence?",
      },
      {
        type: "text",
        body: "Yes. Acadence is open-source and can be self-hosted on your own infrastructure using Docker Compose. The repository includes a production-ready docker-compose.yml with configurations for the backend (FastAPI), frontend (React), WhatsApp service (Node.js), and MongoDB.",
      },
      {
        type: "callout",
        variant: "info",
        title: "Self-Hosting Requirements",
        body: "Minimum server specs for up to 2,000 concurrent users: 4 vCPUs, 8 GB RAM, 100 GB SSD. For larger deployments, contact us for infrastructure recommendations.",
      },
      {
        type: "heading",
        level: 2,
        text: "Can we export our data if we leave?",
      },
      {
        type: "text",
        body: "Absolutely. Data portability is a core principle of Acadence. You can export all your data at any time:",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "All tables and reports support CSV/Excel export from within the UI",
          "Admins can request a full database dump in JSON format at any time",
          "On cancellation, a final full data export is provided within 5 business days at no charge",
          "Data is retained for 60 days after account cancellation before permanent deletion",
        ],
      },
    ],
  },
  {
    slug: "billing",
    section: "faq",
    title: "Billing & Pricing FAQ",
    subtitle:
      "Everything you need to know about pricing, payments, trials, and invoicing.",
    icon: Receipt,
    readTime: "4 min read",
    content: [
      {
        type: "heading",
        level: 2,
        text: "How is Acadence priced?",
      },
      {
        type: "text",
        body: "Acadence uses a per-student, per-year pricing model. The price per student decreases as your institution grows — making it cost-effective for both small colleges and large universities. All 21 modules are included in every plan; there are no module-level add-on fees.",
      },
      {
        type: "callout",
        variant: "info",
        title: "Contact Sales for a Quote",
        body: "Pricing is customised based on the number of enrolled students and your institution's location. Contact our sales team for an exact quote — we respond within one business day.",
      },
      {
        type: "heading",
        level: 2,
        text: "What payment methods are accepted?",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "Bank transfer (NEFT/RTGS/IMPS for Indian institutions)",
          "Credit and debit cards (Visa, Mastercard, Rupay)",
          "UPI (for Indian institutions)",
          "Razorpay and PayU payment links",
          "Stripe (for international institutions billed in USD/EUR/GBP)",
          "Demand draft or cheque (on request for government institutions)",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "Is there a free trial?",
      },
      {
        type: "text",
        body: "Yes. Acadence offers a 30-day free trial with full access to all modules and a pre-loaded sample dataset. No credit card is required to start a trial. At the end of the trial period, you can migrate your trial data to a paid plan seamlessly.",
      },
      {
        type: "heading",
        level: 2,
        text: "Do you offer a discount for annual billing?",
      },
      {
        type: "text",
        body: "Yes. Institutions that pay annually (upfront for 12 months) receive a 15% discount compared to the monthly billing rate. Most institutions opt for annual billing aligned with the academic year.",
      },
      {
        type: "callout",
        variant: "success",
        title: "Multi-Year Discount",
        body: "Institutions signing a 2-year or 3-year contract receive an additional 5–10% discount. Contact sales for details.",
      },
      {
        type: "heading",
        level: 2,
        text: "Are there setup or implementation fees?",
      },
      {
        type: "text",
        body: "For standard cloud deployments, there are no setup fees — onboarding, data migration assistance, and initial training are included in the subscription price. For custom implementations (complex data migrations, API integrations, or on-premise deployments), a one-time professional services fee applies.",
      },
      {
        type: "heading",
        level: 2,
        text: "How does invoicing work?",
      },
      {
        type: "text",
        body: "Invoices are generated automatically and emailed to the billing contact on your account. All invoices are GST-compliant (for Indian institutions) and include itemised line items for the subscription period and student count.",
      },
      {
        type: "list",
        style: "unordered",
        items: [
          "Monthly billing — invoice generated on the 1st of each month",
          "Annual billing — invoice generated at the start of the subscription year",
          "Invoices are available for download in the Admin panel under Settings > Billing",
          "Custom PO numbers can be added to invoices on request",
        ],
      },
      {
        type: "heading",
        level: 2,
        text: "What is your refund policy?",
      },
      {
        type: "text",
        body: "Monthly subscriptions can be cancelled at any time; you will not be charged for the next billing cycle. Refunds for the current month are not provided. For annual subscriptions, a prorated refund is available within the first 30 days of the subscription year. After 30 days, annual subscriptions are non-refundable. If Acadence experiences a service outage of more than 24 hours, a prorated credit is applied to your next invoice.",
      },
      {
        type: "callout",
        variant: "warning",
        title: "Government & NGO Pricing",
        body: "Special pricing is available for government-aided institutions and registered NGOs running educational programs. Please contact sales with supporting documentation.",
      },
    ],
  },
];

export default faq;
